import { createSlice } from '@reduxjs/toolkit';
import copy from 'fast-copy';
import { SelectBoxOption } from '../../../interfaces';
import { Moment } from 'moment';
import { ExchangeData, paperSizes, RevisionHistoryInfo } from 'pdf-generator';
import { orientationOptions } from '../../../screens/documentTemplates/dashboard/addEditTemplate';
import { Attachment } from '../material';
import moment from 'moment-timezone';

export interface AddressValue { include: boolean, value?: string, error?: boolean, errorMessage?: string }
export interface Address {
    name: AddressValue,
    emailId: AddressValue,
    phoneNumber: AddressValue,
    address: AddressValue
}

export interface QuoteItemDetail {
    id: string,
    entityType: SelectBoxOption | null,
    entity: SelectBoxOption | null,
    pricing: SelectBoxOption & { isCalculated: boolean, calculating?: boolean, exchange_rates: ExchangeData } | null,
    partNumber: string,
    description: string,
    unitCost: number,
    quantity: number,
    discount: number,
    leadTime: string,
    totalCost: number
}

export interface QuoteCustomItemDetail {
    id: string,
    material_id: SelectBoxOption | null;
    part_number: string,
    description: string,
    unitCost: number,
    quantity: number,
    discount: number,
    leadTime: string,
    totalCost: number
}

export interface TaxDetail {
    id: string,
    description?: string,
    value?: number
}

export interface CorrespondanceValue {
    include: boolean,
    address: Address,
    sameAs?: keyof Correspondance
}

export interface Correspondance {
    soldBy: CorrespondanceValue,
    soldTo: CorrespondanceValue,
    billFrom: CorrespondanceValue,
    billTo: CorrespondanceValue,
    shipFrom: CorrespondanceValue,
    shipTo: CorrespondanceValue,
}

export interface CoverLetter {
    include: boolean,
    fromAddress?: string,
    fromAddressAs?: keyof Correspondance,
    subject?: string,
    subjectAsName?: boolean,
    content?: string,
    regards?: string,
    name?: string,
    designation?: string,
    attachments?: {
        _id: string,
        file_name: string,
        url: string,
        status: string,
        base64?: string
    }[]
}

export interface TermsAndConditionDetail {
    id: string,
    heading?: string,
    notes?: string
}

export interface CoverPageConfig {
    logo: {
        isActive: boolean,
        src: Attachment | File | null,
        vertical: number,
        horizontal: number,
        width: number,
        height: number,
        attachment_id?: string
    },
    include_cover_page: boolean,
    include_heading: boolean,
    heading: string,
    attachments?: any
}

export interface QuoteReport {
    attachment_id?: string; // Placeholder for attachment details
    status?: 'saved' | 'failed' | 'notUploaded'; // Status of the report
    footer_url?: string; // URL of the footer
    name: string; // Name of the Quote (required)
    orientation?: string; // Orientation of the report, default is 'portrait'
    paper_size?: string; // Size of the paper, default is 'A4'
    show_footer?: boolean; // Flag to indicate if footer should be shown, default is true
    table_header_color?: string; // Color of the table header
    template_id?: string; // Placeholder for template details
    template_type?: string; // Type of the template used
    use_mb_formatter?: boolean; // Flag to indicate if MB formatter should be used, default is false
    include_serial_no?: boolean; // Flag to indicate if serial number should be included, default is false
    processing?: boolean; // Flag to indicate if the report is being processed
}

export type UserFields = { include: boolean, value: SelectBoxOption & { email?: string; user_id?: string; user_email?: string } | null };

// interface User {
//     user_email: string;
//     user_id: string;
//     user_name: string;
// }

// interface RevisionHistoryInfo {
//     _id: string;
//     date_time?: string,
//     approver: User;
//     creator: User;
//     description: string;
//     include_approver: boolean;
//     include_creator: boolean;
//     include_revisor: boolean;
//     revisor: User;
//     version: number;
// }

export interface RevisionInfo {
    date?: string;
    description: string;
    approved_by: UserFields;
    created_by: UserFields;
    referred_by: UserFields;
    history?: RevisionHistoryInfo[];
}

export interface QuoteSliceInterface {
    id: string
    name: string,
    version: number,
    released: boolean,
    sequence_id: string,
    customQuote: boolean,
    revision_info: RevisionInfo,
    status?: SelectBoxOption | null,
    priority?: SelectBoxOption | null,
    currency?: SelectBoxOption | null,
    tags?: SelectBoxOption[] | null,
    hfTemplate?: SelectBoxOption & { orientation?: string, paper_size?: string } | null,
    creationDate?: Moment,
    validTill?: Moment,
    total_quote_price: number,
    total_quote_item_price: number,
    total_quote_tax_price: number,
    quote_report?: QuoteReport,
    quote_item_details: QuoteItemDetail[],
    quote_custom_item_details: QuoteCustomItemDetail[],
    tax_details: TaxDetail[],
    correspondance: Correspondance,
    cover_letter: CoverLetter,
    terms_and_conditions: TermsAndConditionDetail[],
    contents: TermsAndConditionDetail[],
    paper_size: SelectBoxOption | null,
    orientation: SelectBoxOption | null,
    include_cover_letter_from: boolean,
    include_cover_letter_subject: boolean,
    cover_page_config: CoverPageConfig,
    serial_num?: string,
    report_config: {
        include_serial_num: boolean,
        include_revision_table: boolean
    },
    time_zone: string,
    fetched_at: string,
    attachments: Attachment[];
}

export const correspondanceDefaultValue = () => {
    return {
        include: false,
        address: {
            name: { include: false, value: "" },
            emailId: { include: false, value: "" },
            phoneNumber: { include: false, value: "" },
            address: { include: false, value: "" }
        }
    }
}

export const initialState = (): QuoteSliceInterface => {
    return {
        id: "",
        name: "",
        version: 0,
        sequence_id: "",
        released: false,
        customQuote: false,
        status: null,
        priority: null,
        currency: null,
        tags: [],
        hfTemplate: null,
        creationDate: undefined,
        validTill: undefined,
        total_quote_price: 0,
        total_quote_item_price: 0,
        total_quote_tax_price: 0,
        quote_item_details: [],
        quote_custom_item_details: [],
        tax_details: [],
        correspondance: {
            soldBy: correspondanceDefaultValue(),
            soldTo: correspondanceDefaultValue(),
            billFrom: correspondanceDefaultValue(),
            billTo: correspondanceDefaultValue(),
            shipFrom: correspondanceDefaultValue(),
            shipTo: correspondanceDefaultValue(),
        },
        cover_letter: {
            include: false,
            fromAddress: undefined,
            fromAddressAs: undefined,
            subject: undefined,
            subjectAsName: undefined,
            content: undefined,
            regards: undefined,
            name: undefined,
            designation: undefined,
        },
        terms_and_conditions: [],
        contents: [],
        paper_size: paperSizes[4],
        orientation: orientationOptions[0],
        include_cover_letter_from: true,
        include_cover_letter_subject: true,
        cover_page_config: {
            logo: {
                isActive: true,
                src: null,
                vertical: 0,
                horizontal: 0,
                width: 60,
                height: 60,
            },
            include_cover_page: false,
            include_heading: true,
            heading: "",
        },
        serial_num: undefined,
        report_config: {
            include_serial_num: false,
            include_revision_table: false
        },
        quote_report: {
            status: "notUploaded",
            name: "",
            orientation: orientationOptions[0].value,
            paper_size: paperSizes[4].value,
            show_footer: true,
            table_header_color: "#d1c4e9",
            template_type: "hfTemplate",
            use_mb_formatter: false,
            include_serial_no: false,
            processing: false,
        },
        revision_info: {
            description: "",
            approved_by: { include: false, value: null },
            created_by: { include: false, value: null },
            referred_by: { include: false, value: null },
        },
        attachments: [],
        time_zone: moment.tz.guess(),
        fetched_at: new Date().toISOString()
    }
}

export const quoteSlice = createSlice({
    name: 'quote',
    initialState: initialState(),
    reducers: {
        // To Slice by key value pair
        updateQuoteSliceByKeyValue(state, { payload }: { payload: { key: keyof QuoteSliceInterface, value: never } }) {
            let newState: QuoteSliceInterface = copy(state);
            newState[payload.key] = payload.value;
            return { ...state, ...newState }
        },

        // To Reset all values to initialValue
        resetQuoteSlice(state) {
            return { ...state, ...initialState() }
        },

        // To Bulk Update the slice
        updateQuoteSlice(state, { payload }: { payload: Partial<QuoteSliceInterface> }) {
            return { ...state, ...payload }
        }
    }
});

export const {
    updateQuoteSliceByKeyValue,
    resetQuoteSlice,
    updateQuoteSlice,
} = quoteSlice.actions;

export default quoteSlice.reducer;